import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/api";

const TYPES = [
  "Parent call", "Student counselling", "Doubt session",
  "Academic recovery session", "Payment discussion", "Custom",
];

export default function InterventionDialog({ open, onOpenChange, student, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [type, setType] = useState("Academic recovery session");
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.addIntervention(student.id, { type, date, notes });
      toast.success("Intervention scheduled");
      onSaved?.(updated);
      onOpenChange(false);
    } catch {
      toast.error("Could not save intervention");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Schedule Intervention</DialogTitle>
          <DialogDescription>Plan the next best action for {student?.name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-ink-muted">Intervention Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="iv-type" className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-ink-muted">Date</Label>
            <Input data-testid="iv-date" type="date" value={date}
              onChange={(e) => setDate(e.target.value)} className="h-10 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-ink-muted">Notes</Label>
            <Textarea data-testid="iv-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Focus on academic recovery before discussing payment…"
              className="min-h-[90px] rounded-lg" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
          <Button data-testid="save-intervention-btn" onClick={save} disabled={saving}
            className="rounded-lg bg-indigo-brand hover:bg-indigo-deep">
            {saving ? "Saving…" : "Save Intervention"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
